<template lang="pug">
.collection-analytics(v-loading="loading && !analytics")
  .stat-grid
    .stat-card(v-for="stat in stats" :key="stat.label")
      .stat-card__icon(:class="`stat-card__icon--${stat.tone}`")
        fai(type="fal" :icon="stat.icon").size-20
      div
        p.stat-card__value {{ stat.value }}
        p.stat-card__label {{ stat.label }}

  .table-card
    .table-card__head
      div
        h2.card-title Collectors
        p.card-hint(v-if="requiredCount < totalStickers")
          | Completes at {{ requiredCount }} of {{ totalStickers }} stickers
      .d-flex.align-items-center.gap-2
        .search-box
          fai(type="fal" icon="search").size-14
          input(
            v-model="search"
            placeholder="Search collectors…"
            @input="debouncedFetch"
          )
        .segmented
          button.segmented__btn(
            v-for="f in filterOptions"
            :key="f.value"
            type="button"
            :class="{ 'is-active': filter === f.value }"
            @click="setFilter(f.value)"
          ) {{ f.label }}

    table.collectors-table
      thead
        tr
          th Collector
          th Started
          th.collectors-table__progress-col Progress
          th Status
      tbody
        tr(v-for="(row, i) in collectors" :key="i")
          td
            p.collector__name(:class="{ 'is-anonymous': !row.isIdentityDisclosed }")
              | {{ row.isIdentityDisclosed ? (row.name || 'Collector') : 'Anonymous collector' }}
            p.collector__email(v-if="row.isIdentityDisclosed && row.email") {{ row.email }}
          td.collectors-table__muted {{ row.startedAt ? formatDate(row.startedAt) : '—' }}
          td
            .progress
              .progress__track
                .progress__fill(:style="progressStyle(row.collectedCount, requiredCount)")
              span.progress__label {{ row.collectedCount }}/{{ row.totalStickers }}
          td
            span.status-pill(
              :class="row.isCompleted ? 'status-pill--done' : 'status-pill--progress'"
            )
              fai(v-if="row.isCompleted" type="fal" icon="check").size-14
              | {{ row.isCompleted ? 'Completed' : 'In Progress' }}
        tr(v-if="!loading && !collectors.length")
          td.collectors-table__empty(colspan="4") No collectors match your filter.

    .table-card__pager(v-if="totalCollectors > pageSize")
      span Page {{ page }} of {{ pageCount }} · {{ totalCollectors }} collectors
      .d-flex.gap-2
        button.pager-btn(type="button" :disabled="page <= 1 || loading" @click="goPage(page - 1)")
          | Previous
        button.pager-btn(
          type="button"
          :disabled="page >= pageCount || loading"
          @click="goPage(page + 1)"
        ) Next

  .breakdown-card
    h2.card-title.mb-4 Sticker Unlock Breakdown
    p.breakdown-empty(v-if="!analytics?.items?.length") No stickers in this collection yet.
    .breakdown-list(v-else)
      .breakdown-row(v-for="item in analytics.items" :key="item.itemId")
        .breakdown-row__thumb
          img(v-if="item.url" :src="item.url" :alt="item.title")
          fai(v-else type="fal" icon="image").size-18
        .breakdown-row__main
          .breakdown-row__head
            span.breakdown-row__name {{ item.title }}
            span.breakdown-row__count {{ item.collectorCount }}/{{ startedCount }} collectors
          .progress
            .progress__track
              .progress__fill(:style="progressStyle(item.collectorCount, startedCount)")
            span.progress__label {{ item.collectorCount }}/{{ startedCount }}
        .breakdown-row__uses {{ item.codeRedemptions }} uses
</template>

<script lang="ts">
import {
  computed, defineComponent, ref, watch,
} from 'vue';
import dayjs from 'dayjs';
import { ElMessage } from 'element-plus';
import globalShared from '@/shared';
import { findStickerCollectionAnalytics } from '@/graphs/gql';
import type { StickerCollectionAnalytics } from '@/data/stickerCollection.types';

const PAGE_SIZE = 20;

function debounce(fn: () => void, wait: number) {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  return () => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(fn, wait);
  };
}

export default defineComponent({
  name: 'CollectionAnalyticsTab',
  props: {
    collectionId: { type: String, required: true },
    active: { type: Boolean, default: false },
  },
  setup(props) {
    const { makeGraphRequest, getAuthToken } = globalShared();
    const loading = ref(false);
    const analytics = ref<StickerCollectionAnalytics | null>(null);
    const filter = ref('ALL');
    const search = ref('');
    const page = ref(1);

    const filterOptions = [
      { label: 'All', value: 'ALL' },
      { label: 'In-Progress', value: 'IN_PROGRESS' },
      { label: 'Completed', value: 'COMPLETED' },
    ];

    const collectors = computed(() => analytics.value?.collectors ?? []);
    const startedCount = computed(() => analytics.value?.startedCount ?? 0);
    const totalCollectors = computed(() => analytics.value?.totalCollectors ?? 0);
    const totalStickers = computed(() => analytics.value?.totalStickers ?? 0);
    // Completion (and avg. progress) is measured against this, which can be below the total.
    const requiredCount = computed(
      () => analytics.value?.stickersRequiredToComplete || totalStickers.value,
    );
    const pageCount = computed(() => Math.max(1, Math.ceil(totalCollectors.value / PAGE_SIZE)));

    const stats = computed(() => [
      {
        label: 'Started Collection', value: startedCount.value, icon: 'users', tone: 'blue',
      },
      {
        label: 'Completed',
        value: analytics.value?.completedCount ?? 0,
        icon: 'trophy',
        tone: 'teal',
      },
      {
        label: 'In Progress',
        value: analytics.value?.inProgressCount ?? 0,
        icon: 'chart-bar',
        tone: 'amber',
      },
      {
        label: 'Avg. Progress',
        value: `${Math.round(analytics.value?.averageProgressPercent ?? 0)}%`,
        icon: 'chart-bar',
        tone: 'violet',
      },
    ]);

    async function fetchAnalytics() {
      loading.value = true;
      try {
        const response = await makeGraphRequest(
          'query',
          findStickerCollectionAnalytics,
          {
            collectionId: props.collectionId,
            filter: filter.value === 'ALL' ? null : filter.value,
            search: search.value.trim() || null,
            skip: (page.value - 1) * PAGE_SIZE,
            take: PAGE_SIZE,
          },
          getAuthToken(),
        );
        if (response?.errors?.length) {
          ElMessage.error(response.errors[0]?.message || 'Failed to load analytics');
          return;
        }
        analytics.value = response?.data?.findStickerCollectionAnalytics ?? null;
      } catch (e) {
        console.error(e);
        ElMessage.error('Failed to load analytics');
      } finally {
        loading.value = false;
      }
    }

    const debouncedFetch = debounce(() => {
      page.value = 1;
      fetchAnalytics();
    }, 400);

    function setFilter(value: string) {
      filter.value = value;
      page.value = 1;
      fetchAnalytics();
    }

    function goPage(p: number) {
      page.value = p;
      fetchAnalytics();
    }

    // Refetch each time the tab opens so new collectors show up.
    watch(
      () => props.active,
      (isActive) => {
        if (isActive) fetchAnalytics();
      },
      { immediate: true },
    );

    function formatDate(iso: string) {
      return dayjs(iso).format('MMM D, YYYY');
    }

    function progressStyle(value: number, total: number) {
      const pct = total ? Math.min(100, Math.round((value / total) * 100)) : 0;
      return { width: `${pct}%`, background: pct === 100 ? '#0d9488' : '#2dd4bf' };
    }

    return {
      pageSize: PAGE_SIZE,
      loading,
      analytics,
      collectors,
      startedCount,
      totalCollectors,
      totalStickers,
      requiredCount,
      pageCount,
      stats,
      filter,
      search,
      page,
      filterOptions,
      debouncedFetch,
      setFilter,
      goPage,
      formatDate,
      progressStyle,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '../collection-cms';

// max-w-6xl space-y-5
.collection-analytics {
  max-width: 1152px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.stat-card {
  @include card;
  padding: 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;

  &__icon {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;

    &--blue {
      background: $blue-50;
      color: $blue-600;
    }

    &--teal {
      background: $teal-50;
      color: $teal;
    }

    &--amber {
      background: $amber-50;
      color: $amber-600;
    }

    &--violet {
      background: $violet-50;
      color: $violet-600;
    }
  }

  &__value {
    font-size: 24px;
    line-height: 32px;
    font-weight: 700;
    color: $gray-900;
    margin: 0;
  }

  &__label {
    @include text-xs;
    line-height: 1.375;
    color: $gray-500;
    margin: 2px 0 0;
  }
}

.card-title {
  @include text-sm;
  font-weight: 600;
  color: $gray-900;
  margin: 0;
}

.card-hint {
  @include text-xs;
  color: $gray-400;
  margin: 2px 0 0;
}

.table-card {
  @include card;
  overflow: hidden;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid $gray-100;
  }

  &__pager {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    border-top: 1px solid $gray-100;
    @include text-xs;
    color: $gray-500;
  }
}

.search-box {
  display: flex;
  align-items: center;
  gap: 6px;
  background: $gray-50;
  border: 1px solid $gray-200;
  border-radius: 8px;
  padding: 8px 12px;
  color: $gray-500;
  font-size: 14px;

  input {
    width: 160px;
    background: transparent;
    border: none;
    outline: none;
    padding: 0;
    @include text-sm;
    color: $gray-700;
    font-family: inherit;

    &::placeholder {
      color: $gray-400;
    }
  }
}

.segmented {
  display: flex;
  gap: 4px;
  background: $gray-100;
  border-radius: 8px;
  padding: 4px;

  &__btn {
    padding: 4px 12px;
    border-radius: 4px;
    border: none;
    background: transparent;
    @include text-xs;
    font-weight: 500;
    font-family: inherit;
    color: $gray-500;
    cursor: pointer;
    transition: all 0.15s;

    &:hover {
      color: $gray-700;
    }

    &.is-active {
      background: white;
      color: $gray-800;
      box-shadow: 0 1px 2px rgb(0 0 0 / 5%);
    }
  }
}

.collectors-table {
  width: 100%;
  border-collapse: collapse;
  @include text-sm;

  thead tr {
    background: $gray-50;
    border-bottom: 1px solid $gray-100;
  }

  th {
    text-align: left;
    padding: 12px 20px;
    @include text-xs;
    font-weight: 500;
    color: $gray-500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  &__progress-col {
    width: 208px;
  }

  tbody tr {
    transition: background-color 0.15s;

    & + tr {
      border-top: 1px solid $gray-50;
    }

    &:hover {
      background: $gray-50;
    }
  }

  td {
    padding: 14px 20px;
    vertical-align: middle;
  }

  &__muted {
    color: $gray-500;
  }

  &__empty {
    padding: 40px 20px !important;
    text-align: center;
    color: $gray-400;
  }
}

.collector {
  &__name {
    font-weight: 500;
    color: $gray-900;
    margin: 0;

    &.is-anonymous {
      color: $gray-500;
      font-style: italic;
    }
  }

  &__email {
    @include text-xs;
    color: $gray-400;
    margin: 0;
  }
}

.progress {
  display: flex;
  align-items: center;
  gap: 8px;

  &__track {
    flex: 1;
    height: 6px;
    background: $gray-100;
    border-radius: 9999px;
    overflow: hidden;
  }

  &__fill {
    height: 100%;
    border-radius: 9999px;
    transition: width 0.2s;
  }

  &__label {
    width: 40px;
    text-align: right;
    @include text-xs;
    color: $gray-500;
  }
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 9999px;
  @include text-xs;
  font-weight: 500;

  &--done {
    background: $teal-50;
    color: $teal;
  }

  &--progress {
    background: $amber-50;
    color: $amber-600;
  }
}

.pager-btn {
  @include btn-outline;
  @include text-xs;
  padding: 4px 10px;
}

.breakdown-card {
  @include card;
  padding: 20px;
}

.breakdown-empty {
  @include text-sm;
  color: $gray-400;
  margin: 0;
}

.breakdown-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.breakdown-row {
  display: flex;
  align-items: center;
  gap: 12px;

  &__thumb {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border-radius: 8px;
    background: $gray-100;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    color: $gray-300;
    font-size: 12px;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__main {
    flex: 1;
    min-width: 0;
  }

  &__head {
    display: flex;
    justify-content: space-between;
    margin-bottom: 4px;
  }

  &__name {
    @include text-sm;
    font-weight: 500;
    color: $gray-700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__count {
    @include text-xs;
    color: $gray-400;
    margin-left: 8px;
    flex-shrink: 0;
  }

  &__uses {
    width: 64px;
    flex-shrink: 0;
    text-align: right;
    @include text-xs;
    color: $gray-400;
  }
}
</style>
